import { useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

const EMPTY = { current_password: "", new_password: "", confirm_password: "" };

/** Self-service password rotation. Requires the current password + a new one
 * (8+ chars) and confirmation. Backend re-issues fresh auth cookies on success. */
export function ChangePasswordCard() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters"); return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("New password and confirmation don't match"); return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border rounded-lg p-5" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound size={14} className="text-primary" />
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Password</div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Rotate any time — old sessions on other devices continue to work until they expire.
        Use 8+ characters and something you can remember without a manager.
      </p>
      <form onSubmit={submit} className="space-y-3 max-w-md">
        <div>
          <Label>Current password</Label>
          <Input type="password" autoComplete="current-password" required
                 value={form.current_password}
                 onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                 data-testid="cp-current-input" />
        </div>
        <div>
          <Label>New password</Label>
          <Input type="password" autoComplete="new-password" minLength={8} required
                 value={form.new_password}
                 onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                 data-testid="cp-new-input" />
        </div>
        <div>
          <Label>Confirm new password</Label>
          <Input type="password" autoComplete="new-password" minLength={8} required
                 value={form.confirm_password}
                 onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                 data-testid="cp-confirm-input" />
        </div>
        <Button type="submit" disabled={busy} data-testid="cp-submit-btn">
          {busy ? "Updating…" : "Change password"}
        </Button>
      </form>
    </div>
  );
}
